/**
 * PersonCombobox (Phase 5) — searchable typeahead replacing the booking participant pickers.
 *
 * Client-side filter over an already-loaded list (instant, offline-friendly). Prefix-matches the
 * query against first/last/company (full_name token fallback for staff) via personMatchRank,
 * ranked first-name → surname → company → token, inclusive across fields. Labels come pre-composed
 * (personDisplayLabel upstream). `extraOptions` are always-shown specials (external instructor/
 * examiner). `onCreateNew`, when provided, surfaces a "Create new" row for the typed text.
 *
 * Keyboard a11y (matches the AppMenu pattern): ↑/↓ move, Enter selects, Esc closes; click-outside
 * closes. Listbox/option roles + aria-activedescendant.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { personMatchRank, type PersonSearchFields } from '../../lib/opsBoardTypes';
import { C } from './theme';
import { inputStyle } from './atoms';

export interface PersonOption extends PersonSearchFields {
  id: string;
  label: string;
}

interface Props {
  placeholder: string;
  options: PersonOption[];
  onSelect: (id: string) => void;
  extraOptions?: { value: string; label: string }[];
  onCreateNew?: (typed: string) => void;
}

type Item =
  | { kind: 'person'; id: string; label: string }
  | { kind: 'extra'; value: string; label: string }
  | { kind: 'create'; label: string };

export default function PersonCombobox({ placeholder, options, onSelect, extraOptions = [], onCreateNew }: Props) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listId = useRef(`pc-${Math.random().toString(36).slice(2, 8)}`).current;

  const filtered = useMemo(
    () =>
      options
        .map((o) => ({ o, r: personMatchRank(o, query) }))
        .filter((x): x is { o: PersonOption; r: number } => x.r !== null)
        .sort((a, b) => a.r - b.r || a.o.label.localeCompare(b.o.label))
        .map((x) => x.o),
    [options, query],
  );

  const items: Item[] = useMemo(() => {
    const list: Item[] = filtered.map((o) => ({ kind: 'person', id: o.id, label: o.label }));
    for (const e of extraOptions) list.push({ kind: 'extra', value: e.value, label: e.label });
    if (onCreateNew && query.trim()) list.push({ kind: 'create', label: `＋ Create new “${query.trim()}”` });
    return list;
  }, [filtered, extraOptions, onCreateNew, query]);

  // Keep the highlight in range as the item list changes.
  useEffect(() => { setHi((h) => Math.min(h, Math.max(0, items.length - 1))); }, [items.length]);

  // Click-outside closes (mirrors AppMenu).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  function choose(item: Item) {
    if (item.kind === 'person') onSelect(item.id);
    else if (item.kind === 'extra') onSelect(item.value);
    else onCreateNew?.(query.trim());
    setQuery('');
    setOpen(false);
    setHi(0);
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi((h) => Math.min(h + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { if (open && items[hi]) { e.preventDefault(); choose(items[hi]); } }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
  }

  const optStyle = (active: boolean, special: boolean): CSSProperties => ({
    padding: '7px 10px', fontSize: 12, cursor: 'pointer',
    background: active ? C.accentLight : 'transparent',
    color: special ? C.accent : C.text,
    fontWeight: special ? 600 : 400,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  });

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && items[hi] ? `${listId}-${hi}` : undefined}
        placeholder={placeholder}
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setHi(0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        style={inputStyle}
      />
      {open && items.length > 0 && (
        <div
          id={listId}
          role="listbox"
          style={{
            position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0, zIndex: 50,
            background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)', maxHeight: 240, overflowY: 'auto', padding: '3px 0',
          }}
        >
          {items.map((it, idx) => (
            <div
              key={it.kind === 'person' ? `p-${it.id}` : it.kind === 'extra' ? `e-${it.value}` : 'create'}
              id={`${listId}-${idx}`}
              role="option"
              aria-selected={idx === hi}
              onMouseDown={(e) => { e.preventDefault(); choose(it); }}
              onMouseEnter={() => setHi(idx)}
              style={optStyle(idx === hi, it.kind !== 'person')}
            >
              {it.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
